import { GetStaticProps } from 'next';
import Script from 'next/script';

import {
	BodyTextFieldsFragment,
	CookiePageFieldsFragment,
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	Enum_Componentinternaldefaultmoduleoptions_Padding,
	PageInformationFieldsFragment
} from 'api/graphql-types';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetCookie } from 'api/graphql-helper';

import { Head } from 'modules/head/head';
import Layout from 'modules/layout/layout';
import { BaseModule } from 'modules/base-module/base-module';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import BodyText from 'modules/body-text/normal/normal-body-text';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';

interface ICookiesPageProps extends IBasePageProps {
	cookiePage?: CookiePageFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
}

const CookiesPage = (props: ICookiesPageProps): React.JSX.Element => {
	const seoModule = props?.cookiePage?.seo;

	const headerDefaultOptions = {
		backgroundColor: props.cookiePage?.header
			?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
		padding: 'none' as Enum_Componentinternaldefaultmoduleoptions_Padding
	};

	return (
		<Layout
			contentId={props?.id}
			typeName={props?.cookiePage?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<IntroHeader module={props.cookiePage?.header as IModule} fullPath={ROUTES.COOKIES.fullPath} />
			</BaseModule>
			<BaseModule>
				<BodyText module={props.cookiePage?.bodyText as BodyTextFieldsFragment} small />
			</BaseModule>
			<BaseModule>
				<div id="CookieDeclaration" />
				<Script src={`https://consent.cookiebot.com/${process.env.NEXT_PUBLIC_COOKIEBOT_API_KEY}/cd.js`} />
			</BaseModule>
		</Layout>
	);
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, publicationState } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'PREVIEW') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetCookie(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, cookiePage } = result.props.data;

		if (!cookiePage?.data?.attributes) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				key: cookiePage?.data?.id,
				pageInformation: pageInformation?.data?.attributes,
				cookiePage: cookiePage.data?.attributes,
				id: cookiePage.data?.id,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						contentType: 'cookie-page',
						contentTypeId: cookiePage?.data?.id || ''
					}
				}
			},
			revalidate: Number(process.env.REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in cookies page', error);

		return {
			notFound: true
		};
	}
};

export default CookiesPage;
